import { RootLogger } from 'loglevel';
import { datadogLogs } from '@datadog/browser-logs';
import { scrubUrl } from '@/providers/datadog.utils';
import { posthog } from '@/providers/posthog';
import { PAGE_LOAD_ID } from './page-load.ts';
import { WeavyLog } from './types.ts';
import type { StatusType } from '@datadog/browser-logs';

const toDatadogStatus = (level: string): StatusType => {
  switch (level) {
    case 'error':
      return 'error';
    case 'warn':
      return 'warn';
    case 'info':
      return 'info';
    case 'trace':
    case 'debug':
    default:
      return 'debug';
  }
};

const toError = (error: WeavyLog['error']): Error | undefined => {
  // This is here because the error is serialized to a string and then parsed back into an object by a previous middleware.
  if (!error) return undefined;
  if (typeof error === 'string') return new Error(error);

  const errorLike = error as { message?: string; name?: string; stack?: string };
  const reconstructed = new Error(errorLike.message ?? 'Error');
  if (errorLike.name) reconstructed.name = errorLike.name;
  if (errorLike.stack) reconstructed.stack = errorLike.stack;
  return reconstructed;
};

type LocationLike = { url?: unknown; referrer?: unknown };

const scrubAdditionalInfoLocation = (additionalInfo: Record<string, unknown>): Record<string, unknown> => {
  const { location } = additionalInfo;
  if (!location || typeof location !== 'object') return additionalInfo;

  const loc = location as LocationLike;
  return {
    ...additionalInfo,
    location: {
      ...loc,
      ...(typeof loc.url === 'string' ? { url: scrubUrl(loc.url) } : {}),
      ...(typeof loc.referrer === 'string' ? { referrer: scrubUrl(loc.referrer) } : {}),
    },
  };
};

const forwardToDatadog = (formatted: string, appVersion: string): void => {
  const weavyLog = JSON.parse(formatted) as WeavyLog;
  const status = toDatadogStatus(weavyLog.level);

  // Debug-level logs are too noisy/costly to ship to Datadog; they still print
  // to the console via the other middlewares.
  if (status === 'debug') return;

  const { originalStackTrace, ...additionalInfo } = weavyLog.additionalInfo || {};

  datadogLogs.logger.log(
    weavyLog.msg,
    {
      context: weavyLog.context,
      traceId: weavyLog.traceId,
      sequenceId: weavyLog.sequenceId,
      // The SDK's global context has it too, but misses logs buffered before the SDK starts.
      pageLoadId: PAGE_LOAD_ID,
      user: {
        id: weavyLog.userData?.id,
      },
      posthogRecordingUrl: status === 'error' ? posthog?.get_session_replay_url() : undefined,
      location: scrubUrl(weavyLog.location),
      stacktrace: Array.isArray(originalStackTrace) ? originalStackTrace.join('\n') : undefined,
      appVersion,
      ...scrubAdditionalInfoLocation(additionalInfo),
    },
    status,
    toError(weavyLog.error),
  );
};

export const datadogLogMiddleware = (logger: RootLogger, appVersion: string): void => {
  const originalMethodFactory = logger.methodFactory;
  logger.methodFactory = (methodName, logLevel, loggerName) => {
    const logFunc = originalMethodFactory(methodName, logLevel, loggerName);

    return (...message: unknown[]) => {
      const [formatted] = message;
      if (typeof formatted === 'string') {
        try {
          forwardToDatadog(formatted, appVersion);
        } catch {
          // Not a formatted WeavyLog payload; leave Datadog forwarding to the next emit.
        }
      }
      logFunc(...message);
    };
  };

  logger.setLevel(logger.getLevel());
};
